import { useState } from 'react'
import { motion } from 'framer-motion'
import {
  Bot,
  Brain,
  Layers,
  Cpu,
  Sparkles,
  ArrowRight,
} from 'lucide-react'
import Slide, { StaggerContainer, StaggerItem } from '../components/Slide'

interface SlideProps {
  isActive: boolean
}

const futureTrends = [
  {
    year: '2025',
    title: 'Year of the Agent',
    description: '99% of developers exploring AI agents. 85% use AI tools daily. Multimodal AI (text, image, voice) becoming mainstream.',
    icon: Bot,
    color: 'from-electric-cyan to-blue-500',
    isNow: true,
    source: 'IBM Survey 2025',
  },
  {
    year: '2026',
    title: 'Mainstream Agent Adoption',
    description: '70% of dev workflows AI-powered. 80% of engineers need to reskill. 61% enterprises redesigning systems AI-first.',
    icon: Brain,
    color: 'from-electric-purple to-purple-600',
    isNow: false,
    source: 'Gartner & Deloitte',
  },
  {
    year: '2027',
    title: 'LLM-Native Engineering',
    description: '55% of teams building LLM features. 50% engineers use ML-powered coding tools. AI agent paradigm firmly established.',
    icon: Layers,
    color: 'from-aurora-teal to-teal-600',
    isNow: false,
    source: 'Gartner Predictions',
  },
  {
    year: '2028',
    title: 'Autonomous Decisions',
    description: '15% of work decisions made autonomously by AI. 33% of enterprise software includes agentic AI capabilities.',
    icon: Cpu,
    color: 'from-electric-magenta to-pink-600',
    isNow: false,
    source: 'Gartner Predictions',
  },
  {
    year: '2030+',
    title: 'Developer as Orchestrator',
    description: 'Shift from coding to coordinating AI ecosystems. Human skills: judgment, architecture, strategy, and ethics.',
    icon: Sparkles,
    color: 'from-electric-orange to-orange-600',
    isNow: false,
    source: 'Industry Analysis',
  },
]

export default function FutureTrendsSlide({ isActive: _isActive }: SlideProps) {
  const [activeTrend, setActiveTrend] = useState<number | null>(0)

  return (
    <Slide>
      <StaggerContainer className="h-full flex flex-col">
        {/* Header */}
        <StaggerItem>
          <div className="mb-6 text-center">
            <h2 className="font-display text-4xl md:text-5xl font-bold text-white mb-3">
              Future <span className="text-gradient">Trends</span>
            </h2>
            <p className="text-lg text-white/60">
              What's next in AI-powered development
            </p>
          </div>
        </StaggerItem>

        {/* Timeline */}
        <StaggerItem className="flex-1">
          <div className="relative h-full flex items-center">
            {/* Timeline line */}
            <div className="absolute left-0 right-0 top-1/2 h-1 bg-gradient-to-r from-electric-cyan via-electric-purple to-electric-magenta rounded-full" />

            {/* Timeline nodes */}
            <div className="relative w-full flex justify-between items-center px-4 md:px-8">
              {futureTrends.map((trend, index) => {
                const Icon = trend.icon
                const isActive = activeTrend === index

                return (
                  <motion.div
                    key={trend.year}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 + index * 0.1 }}
                    className="relative flex flex-col items-center"
                    onMouseEnter={() => setActiveTrend(index)}
                    onClick={() => setActiveTrend(activeTrend === index ? null : index)}
                  >
                    {/* Node */}
                    <motion.div
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.95 }}
                      className={`relative z-10 w-14 h-14 md:w-16 md:h-16 rounded-2xl cursor-pointer transition-all duration-300 flex items-center justify-center bg-gradient-to-br ${trend.color} ${
                        isActive ? 'ring-4 ring-white/30 shadow-lg' : ''
                      } ${trend.isNow ? 'animate-pulse-glow' : ''}`}
                    >
                      <Icon className="w-7 h-7 md:w-8 md:h-8 text-white" />
                      {trend.isNow && (
                        <span className="absolute -top-2 -right-2 px-2 py-0.5 rounded-full bg-electric-cyan text-xs font-bold text-space-900">
                          NOW
                        </span>
                      )}
                    </motion.div>

                    {/* Year label */}
                    <span
                      className={`mt-3 text-sm font-mono transition-colors ${
                        isActive ? 'text-electric-cyan' : 'text-white/60'
                      }`}
                    >
                      {trend.year}
                    </span>

                    {/* Detail card (shows on active) */}
                    {isActive && (
                      <motion.div
                        initial={{ opacity: 0, y: 10, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 10, scale: 0.95 }}
                        className="absolute top-full mt-6 glass-strong rounded-xl p-4 min-w-[220px] max-w-[280px] z-20 text-center"
                      >
                        <h4 className="text-lg text-white font-semibold mb-2">
                          {trend.title}
                        </h4>
                        <p className="text-base text-white/60">
                          {trend.description}
                        </p>
                        <p className="text-xs text-white/40 mt-2">
                          {trend.source}
                        </p>
                        <div
                          className={`mt-3 w-full h-1 rounded-full bg-gradient-to-r ${trend.color}`}
                        />
                      </motion.div>
                    )}
                  </motion.div>
                )
              })}
            </div>
          </div>
        </StaggerItem>

        {/* Key insight */}
        <StaggerItem>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1 }}
            className="mt-8"
          >
            <div className="glass rounded-2xl p-6 max-w-3xl mx-auto">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-electric-cyan/20 to-electric-purple/20 flex items-center justify-center flex-shrink-0">
                  <Sparkles className="w-6 h-6 text-electric-cyan" />
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-white mb-2">
                    The Developer Evolution
                  </h4>
                  <p className="text-white/60 text-base">
                    Despite AI handling ~50% of code in 2025, demand for developers remains strong.
                    The role shifts from writing code to guiding, validating, and orchestrating AI.
                    Critical thinking, architecture, and business strategy become core skills.
                  </p>
                  <p className="text-xs text-white/40 mt-2">
                    Sources: IBM • Gartner • Deloitte • JetBrains State of Developer 2025
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </StaggerItem>

        {/* Navigation hint and caution */}
        <StaggerItem>
          <div className="mt-4 mb-[100px] text-center">
            <motion.div
              animate={{ x: [0, 5, 0] }}
              transition={{ duration: 1.5, repeat: Infinity }}
              className="flex items-center justify-center gap-2 text-white/40 text-sm mb-3"
            >
              <span>Hover or tap nodes to explore</span>
              <ArrowRight className="w-4 h-4" />
            </motion.div>
            <p className="text-xs text-white/30">
              Note: Gartner predicts 40% of agentic AI projects may be canceled by 2027 due to cost/risk challenges
            </p>
          </div>
        </StaggerItem>
      </StaggerContainer>
    </Slide>
  )
}
